import React, { useCallback, useMemo } from 'react';
import * as Styles from './Services.styles';
import withErrorBoundary from '@/utils/errorBoundary';
import { SERVICES_IDS } from './Services.enum';
import { useIsMobile } from '@/hooks/useIsMobile/useIsMobile';
import cardAdsBr from '@/pages/Home/media/services/card-ads-br.png';
import cardAdsMobileBr from '@/pages/Home/media/services/card-ads-mobile-br.png';
import cardAdsEs from '@/pages/Home/media/services/card-ads-es.png';
import cardAdsFullEs from '@/pages/Home/media/services/card-ads-full-es.png';
import cardAdsMobileEs from '@/pages/Home/media/services/card-ads-mobile-es.png';
import cardAdsFullBr from '@/pages/Home/media/services/card-ads-full-br.png';
import cardAiAgentsEs from '@/pages/Home/media/services/card-ai-agent-es.png';
import cardAiAgentsBr from '@/pages/Home/media/services/card-ai-agent-br.png';
import cardAiAgentsMobileBr from '@/pages/Home/media/services/card-ai-agent-mobile-br.png';
import cardAiAgentsMobileEs from '@/pages/Home/media/services/card-ai-agent-mobile-es.png';
import cardEmailBr from '@/pages/Home/media/services/card-email-br.png';
import cardEmailMobileBr from '@/pages/Home/media/services/card-email-mobile-br.png';
import cardEmailEs from '@/pages/Home/media/services/card-email-es.png';
import cardEmailMobileEs from '@/pages/Home/media/services/card-email-mobile-es.png';
import cardVpsN8nBr from '@/pages/Home/media/services/card-vps-n8n-br.png';
import cardVpsN8nMobileBr from '@/pages/Home/media/services/card-vps-n8n-mobile-br.png';
import cardVpsN8nEs from '@/pages/Home/media/services/card-vps-n8n-es.png';
import cardVpsN8nMobileEs from '@/pages/Home/media/services/card-vps-n8n-mobile-es.png';
import { isBRServer } from '@/utils/Validators/validation';

const IMAGES_BR = {
  [SERVICES_IDS.ADS]: {
    desktop: cardAdsBr,
    mobile: cardAdsMobileBr,
  },
  [SERVICES_IDS.ADS_FULL]: {
    desktop: cardAdsFullBr,
    mobile: cardAdsMobileBr,
  },
  [SERVICES_IDS.AI_AGENTS]: {
    desktop: cardAiAgentsBr,
    mobile: cardAiAgentsMobileBr,
  },
  [SERVICES_IDS.VPS_N8N]: {
    desktop: cardVpsN8nBr,
    mobile: cardVpsN8nMobileBr,
  },
  [SERVICES_IDS.EMAIL]: {
    desktop: cardEmailBr,
    mobile: cardEmailMobileBr,
  },
};

const IMAGES_ES = {
  [SERVICES_IDS.ADS]: {
    desktop: cardAdsEs,
    mobile: cardAdsMobileEs,
  },
  [SERVICES_IDS.ADS_FULL]: {
    desktop: cardAdsFullEs,
    mobile: cardAdsMobileEs,
  },
  [SERVICES_IDS.AI_AGENTS]: {
    desktop: cardAiAgentsEs,
    mobile: cardAiAgentsMobileEs,
  },
  [SERVICES_IDS.VPS_N8N]: {
    desktop: cardVpsN8nEs,
    mobile: cardVpsN8nMobileEs,
  },
  [SERVICES_IDS.EMAIL]: {
    desktop: cardEmailEs,
    mobile: cardEmailMobileEs,
  },
};

const IMAGES = id => (isBRServer ? IMAGES_BR[id] : IMAGES_ES[id]);

const CARDS = {
  [SERVICES_IDS.ADS]: {
    id: SERVICES_IDS.ADS,
    image: IMAGES(SERVICES_IDS.ADS),
    alt: 'card-ads',
  },
  [SERVICES_IDS.ADS_FULL]: {
    id: SERVICES_IDS.ADS_FULL,
    image: IMAGES(SERVICES_IDS.ADS_FULL),
    alt: 'card-ads-full',
  },
  [SERVICES_IDS.AI_AGENTS]: {
    id: SERVICES_IDS.AI_AGENTS,
    image: IMAGES(SERVICES_IDS.AI_AGENTS),
    alt: 'card-ai-agents',
  },
  [SERVICES_IDS.VPS_N8N]: {
    id: SERVICES_IDS.VPS_N8N,
    image: IMAGES(SERVICES_IDS.VPS_N8N),
    alt: 'card-vps-n8n',
  },
  [SERVICES_IDS.EMAIL]: {
    id: SERVICES_IDS.EMAIL,
    image: IMAGES(SERVICES_IDS.EMAIL),
    alt: 'card-email',
  },
};

const Services = ({ cardsActions, agentFactoryAvailable }) => {
  const isMobile = useIsMobile();

  const filteredCards = useMemo(
    () => (agentFactoryAvailable
      ? Object.values(CARDS).filter(card => card.id !== SERVICES_IDS.ADS_FULL)
      : Object.values(CARDS).filter(
        card => card.id !== SERVICES_IDS.ADS && card.id !== SERVICES_IDS.AI_AGENTS,
      )),
    [agentFactoryAvailable],
  );

  const handleCardClick = useCallback(
    (cardId) => {
      const action = cardsActions[cardId];
      if (action?.onClick) {
        action.onClick();
      }
    },
    [cardsActions],
  );

  return (
    <Styles.Wrapper data-testid="services-wrapper" $hasHighlightCard={agentFactoryAvailable}>
      {filteredCards.map(card => (
        <Styles.Card key={card.alt} $cardId={card.id}>
          <Styles.Image
            src={isMobile ? card.image.mobile : card.image.desktop}
            alt={card.alt}
            onClick={() => handleCardClick(card.id)}
          />
        </Styles.Card>
      ))}
    </Styles.Wrapper>
  );
};

export default withErrorBoundary(Services, 'Services');
